import { supabase, getSchema } from '@/lib/supabase';
import {
  mapAttachmentFromDb,
  mapAttachmentViewFromDb,
  type CustomerInvoiceAttachment,
  type CustomerInvoiceAttachmentView,
  type UploadAttachmentParams,
  type UpdateAttachmentParams,
  type AttachmentFilters,
} from '../types';

/**
 * Get the storage bucket name based on environment
 */
function getBucketName(): string {
  const schema = getSchema();
  return schema === 'testing' ? 'customer-invoice-attachments-testing' : 'customer-invoice-attachments';
}

/**
 * Build the storage path for an attachment file
 * Format: {business_id}/{project_id}/{attachment_id}.{ext}
 */
function buildStoragePath(
  businessId: string,
  projectId: string,
  attachmentId: string,
  fileName: string
): string {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  return `${businessId}/${projectId}/${attachmentId}.${extension}`;
}

/**
 * Upload an attachment file to storage and create database record
 */
export async function uploadAttachment(
  params: UploadAttachmentParams
): Promise<CustomerInvoiceAttachment> {
  const {
    file,
    businessId,
    projectId,
    uploadedBy,
    attachmentType,
    notes,
  } = params;

  const bucketName = getBucketName();
  const attachmentId = crypto.randomUUID();
  const filePath = buildStoragePath(businessId, projectId, attachmentId, file.name);

  // 1. Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading attachment file:', uploadError);
    throw uploadError;
  }

  // 2. Create database record
  const attachmentRecord = {
    id: attachmentId,
    business_id: businessId,
    project_id: projectId,
    uploaded_by: uploadedBy,
    file_name: file.name,
    file_path: filePath,
    file_size_bytes: file.size,
    mime_type: file.type || null,
    attachment_type: attachmentType,
    notes: notes || null,
  };

  const { data, error: insertError } = await supabase
    .from('customer_invoice_attachments')
    .insert(attachmentRecord)
    .select()
    .single();

  if (insertError) {
    // Try to clean up uploaded file
    await supabase.storage.from(bucketName).remove([filePath]);
    console.error('Error creating attachment record:', insertError);
    throw insertError;
  }

  return mapAttachmentFromDb(data);
}

/**
 * Get attachments for a specific project
 */
export async function getProjectAttachments(
  projectId: string
): Promise<CustomerInvoiceAttachmentView[]> {
  const { data, error } = await supabase
    .from('v_customer_invoice_attachments')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching project attachments:', error);
    throw error;
  }

  return (data || []).map(mapAttachmentViewFromDb);
}

/**
 * Get attachments with optional filters
 */
export async function getAttachments(
  businessId: string,
  filters?: AttachmentFilters
): Promise<CustomerInvoiceAttachmentView[]> {
  let query = supabase
    .from('v_customer_invoice_attachments')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (filters?.projectId) {
    query = query.eq('project_id', filters.projectId);
  }

  if (filters?.attachmentType) {
    query = query.eq('attachment_type', filters.attachmentType);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching attachments:', error);
    throw error;
  }

  return (data || []).map(mapAttachmentViewFromDb);
}

/**
 * Get a single attachment by ID
 */
export async function getAttachmentById(
  id: string
): Promise<CustomerInvoiceAttachmentView | null> {
  const { data, error } = await supabase
    .from('v_customer_invoice_attachments')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching attachment:', error);
    throw error;
  }

  return data ? mapAttachmentViewFromDb(data) : null;
}

/**
 * Update attachment metadata (type, notes)
 */
export async function updateAttachment(
  params: UpdateAttachmentParams
): Promise<CustomerInvoiceAttachment> {
  const { id, ...updates } = params;

  // Build the update object with snake_case keys
  const updateData: Record<string, unknown> = {};
  if (updates.attachmentType !== undefined) {
    updateData.attachment_type = updates.attachmentType;
  }
  if (updates.notes !== undefined) {
    updateData.notes = updates.notes;
  }

  const { data, error } = await supabase
    .from('customer_invoice_attachments')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating attachment:', error);
    throw error;
  }

  return mapAttachmentFromDb(data);
}

/**
 * Delete an attachment (removes from storage and database)
 */
export async function deleteAttachment(id: string, filePath: string): Promise<void> {
  const bucketName = getBucketName();

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (storageError) {
    console.error('Error deleting attachment from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('customer_invoice_attachments')
    .delete()
    .eq('id', id);

  if (dbError) {
    console.error('Error deleting attachment from database:', dbError);
    throw dbError;
  }
}

/**
 * Bulk delete attachments
 */
export async function bulkDeleteAttachments(
  items: { id: string; filePath: string }[]
): Promise<void> {
  const bucketName = getBucketName();
  const filePaths = items.map((item) => item.filePath);
  const ids = items.map((item) => item.id);

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove(filePaths);

  if (storageError) {
    console.error('Error deleting attachments from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('customer_invoice_attachments')
    .delete()
    .in('id', ids);

  if (dbError) {
    console.error('Error deleting attachments from database:', dbError);
    throw dbError;
  }
}

/**
 * Get a signed URL for viewing/downloading an attachment file
 */
export async function getAttachmentUrl(
  filePath: string,
  expiresIn: number = 3600
): Promise<string> {
  const bucketName = getBucketName();

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Error creating signed URL:', error);
    throw error;
  }

  return data.signedUrl;
}

/**
 * Get signed URLs for multiple files (batch)
 */
export async function getAttachmentUrls(
  filePaths: string[],
  expiresIn: number = 3600
): Promise<Map<string, string>> {
  const bucketName = getBucketName();
  const urlMap = new Map<string, string>();

  // Supabase doesn't have batch signed URL, so we do them in parallel
  const results = await Promise.allSettled(
    filePaths.map(async (filePath) => {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return { filePath, url: data.signedUrl };
    })
  );

  for (const result of results) {
    if (result.status === 'fulfilled') {
      urlMap.set(result.value.filePath, result.value.url);
    }
  }

  return urlMap;
}
